import { ATTACHMENT_ACCEPT, ATTACHMENT_MAX_BYTES, attachmentContentType } from '@loot/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Download, EyeOff, Paperclip } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, ApiError, type PurchaseDetail } from '../../api.ts';
import { FormModal, FormSection } from '../../components/FormModal.tsx';
import { ConfirmDialog } from '../../components/Modal.tsx';
import { Button, Field, Panel, Select } from '../../components/ui.tsx';
import { MasterDeleteButton } from '../../components/MasterDelete.tsx';
import { useFormat } from './common.tsx';

/** Confere tamanho e tipo antes de enviar (o servidor confere de novo). */
export function checkFile(file: File): ApiError | null {
  if (!attachmentContentType(file.name)) {
    return new ApiError(400, 'file_type_not_allowed');
  }
  if (file.size > ATTACHMENT_MAX_BYTES) return new ApiError(413, 'file_too_large');
  if (file.size === 0) return new ApiError(400, 'file_empty');
  return null;
}

/** Envia um anexo para a compra. O item vai antes do arquivo, como o servidor espera. */
export function uploadAttachment(requestId: string, file: File, itemId?: string | null) {
  const form = new FormData();
  if (itemId) form.append('itemId', itemId);
  form.append('file', file, file.name);
  return api.upload<{ id: string }>(`/purchasing/requests/${requestId}/attachments`, form);
}

/** Anexos da compra: lista, download e ocultar; "Anexar arquivo" abre a janela de envio. */
export function AttachmentsPanel({ request }: { request: PurchaseDetail }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [hiding, setHiding] = useState<string | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['purchasing'] });
  const hide = useMutation({
    mutationFn: (attachmentId: string) =>
      api.post(`/purchasing/requests/${request.id}/attachments/${attachmentId}/remove`),
    onSuccess: async () => {
      setHiding(null);
      await refresh();
    },
  });

  const itemLabel = (id: string | null) => {
    const item = request.items.find((i) => i.id === id);
    return item
      ? t('purchasing.attachments.forItem', { n: item.lineNo, item: item.description })
      : null;
  };

  return (
    <Panel
      title={t('purchasing.attachments.title')}
      action={
        request.actions.attach ? (
          <Button variant="secondary" onClick={() => setAdding(true)}>
            <Paperclip size={16} aria-hidden /> {t('purchasing.attachments.add')}
          </Button>
        ) : undefined
      }
    >
      {request.attachments.length === 0 ? (
        <p className="text-sm text-muted">
          {request.actions.attach
            ? t('purchasing.attachments.emptyCanAdd')
            : t('purchasing.attachments.empty')}
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-rule">
          {request.attachments.map((a) => (
            <li key={a.id} className="flex flex-wrap items-center gap-3 py-2">
              <Paperclip size={16} aria-hidden className="text-muted" />
              <div className="flex min-w-0 flex-1 flex-col">
                <a
                  href={`/api/purchasing/requests/${request.id}/attachments/${a.id}`}
                  className="truncate text-sm text-brand-text hover:underline"
                >
                  {a.fileName}
                </a>
                <span className="text-xs text-muted">
                  {fmt.bytes(a.sizeBytes)} · {a.uploadedBy.name ?? '—'} ·{' '}
                  {fmt.dateTime(a.createdAt)}
                  {itemLabel(a.itemId) && <> · {itemLabel(a.itemId)}</>}
                </span>
              </div>
              <a
                href={`/api/purchasing/requests/${request.id}/attachments/${a.id}`}
                className="inline-flex items-center gap-1 text-sm text-muted hover:text-ink"
              >
                <Download size={16} aria-hidden /> {t('purchasing.attachments.download')}
              </a>
              {a.canRemove && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    hide.reset();
                    setHiding(a.id);
                  }}
                >
                  <EyeOff size={16} aria-hidden /> {t('purchasing.attachments.hide')}
                </Button>
              )}
              <MasterDeleteButton table="purchasing.request_attachment" id={a.id} compact />
            </li>
          ))}
        </ul>
      )}

      {adding && <AttachDialog request={request} onClose={() => setAdding(false)} />}
      <ConfirmDialog
        open={hiding !== null}
        title={t('purchasing.attachments.hideTitle')}
        description={t('purchasing.attachments.hideHint')}
        confirmLabel={t('purchasing.attachments.hide')}
        busy={hide.isPending}
        error={hide.error}
        onConfirm={() => hiding && hide.mutate(hiding)}
        onCancel={() => setHiding(null)}
      />
    </Panel>
  );
}

/** Anexar arquivo (guia visual, variação 4): o arquivo e, com mais de um item, sobre qual item. */
function AttachDialog({ request, onClose }: { request: PurchaseDetail; onClose: () => void }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [itemId, setItemId] = useState('');
  const [checkError, setCheckError] = useState<ApiError | null>(null);
  const send = useMutation({
    mutationFn: (f: File) => uploadAttachment(request.id, f, itemId || null),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['purchasing'] });
      onClose();
    },
  });
  return (
    <FormModal
      open
      size="md"
      title={t('purchasing.attachments.add')}
      dirty={file !== null}
      saving={send.isPending}
      error={checkError ?? send.error}
      saveLabel={t('purchasing.attachments.send')}
      onSave={() => {
        if (!file) return;
        const problem = checkFile(file);
        setCheckError(problem);
        if (!problem) send.mutate(file);
      }}
      onClose={onClose}
    >
      <FormSection columns={1}>
        <Field
          label={t('purchasing.attachments.file')}
          hint={
            file ? `${file.name} · ${fmt.bytes(file.size)}` : t('purchasing.attachments.limits')
          }
          required
        >
          <input
            id="pr-attachment"
            type="file"
            required
            accept={ATTACHMENT_ACCEPT}
            className="text-sm"
            onChange={(ev) => {
              setFile(ev.target.files?.[0] ?? null);
              setCheckError(null);
            }}
          />
        </Field>
        {request.items.length > 1 && (
          <Field label={t('purchasing.attachments.item')}>
            <Select
              id="pr-attachment-item"
              value={itemId}
              onChange={(ev) => setItemId(ev.target.value)}
            >
              <option value="">{t('purchasing.attachments.wholeRequest')}</option>
              {request.items.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.lineNo}. {i.description}
                </option>
              ))}
            </Select>
          </Field>
        )}
      </FormSection>
    </FormModal>
  );
}
